import { extractDateTimeStamp } from '../utils/date-timestamp-extraction';
import { ViewStatusObject, AnalysisAnnotationReference } from '../analysis';
import { GaitType, RecordingAnalysisScaling, RecordingStatusType } from '../types';

export interface RecordingAnalysis {
  comment: string | null;
  declineReason?: string[];
  estimatedTimeLeft: RecordingAnalysisTime | null;
  estimatedTimings: RecordingAnalysisEstimatedTimings | null;
  estimatedTotalTime: RecordingAnalysisTime | null;
  graphImg: RecordingGraphImageListItem[] | null;
  longeingDirection: string | null;
  status: RecordingStatusType;
  summary: RecordingAnalysisSummary;
  thumbnail: string | null;
  viewStatus: ViewStatusObject;
}

export class RecordingAnalysisClass implements RecordingAnalysis {
  comment: string | null;
  declineReason?: string[];
  estimatedTimeLeft: RecordingAnalysisTime | null;
  estimatedTimings: RecordingAnalysisEstimatedTimings | null;
  estimatedTotalTime: RecordingAnalysisTime | null;
  graphImg: RecordingGraphImageListItem[] | null;
  longeingDirection: string | null;
  status: RecordingStatusType;
  summary: RecordingAnalysisSummary;
  thumbnail: string | null;
  viewStatus: ViewStatusObject;

  constructor(o: any = {}) {
    this.comment = o['comment'] ?? null;
    this.declineReason = o['declineReason'] ?? null;
    this.estimatedTimeLeft = o['estimatedTimeLeft']
      ? new RecordingAnalysisTimeClass(o['estimatedTimeLeft'])
      : null;
    this.estimatedTimings = o['estimatedTimings']
      ? new RecordingAnalysisEstimatedTimingsClass(o['estimatedTimings'])
      : null;
    this.estimatedTotalTime = o['estimatedTotalTime']
      ? new RecordingAnalysisTimeClass(o['estimatedTotalTime'])
      : null;
    this.graphImg = o['graphImg']
      ? o['graphImg'].map(
          (item: object) => new RecordingGraphImageListItemClass(item)
        )
      : null;
    this.longeingDirection = o['longeingDirection'] ?? null;
    this.status = o['status'] ?? null;
    this.summary = new RecordingAnalysisSummaryClass(o['summary'] ?? {});
    this.thumbnail = o['thumbnail'] ?? null;
    this.viewStatus = o['viewStatus'] ?? null;
  }
}

export interface RecordingAnalysisTime {
  time: number;
  timeStamp: Date | null;
}

export class RecordingAnalysisTimeClass implements RecordingAnalysisTime {
  time: number;
  timeStamp: Date | null;

  constructor(o: any = {}) {
    this.time = o['time'] ?? null;
    this.timeStamp = extractDateTimeStamp(o['timeStamp']);
  }
}

export interface RecordingAnalysisEstimatedTimings {
  timeLeft: RecordingAnalysisTime;
  totalTime: RecordingAnalysisTime;
}

export class RecordingAnalysisEstimatedTimingsClass
  implements RecordingAnalysisEstimatedTimings
{
  timeLeft: RecordingAnalysisTime;
  totalTime: RecordingAnalysisTime;

  constructor(o: any = {}) {
    this.timeLeft = new RecordingAnalysisTimeClass(o['timeLeft'] ?? {});
    this.totalTime = new RecordingAnalysisTimeClass(o['totalTime'] ?? {});
  }
}

export interface RecordingGraphImageListItem {
  image: string | null;
  juliaHead: string | null;
  thumbnail: string | null;
  juliaHind: string | null;
}

export class RecordingGraphImageListItemClass
  implements RecordingGraphImageListItem
{
  image: string | null;
  juliaHead: string | null;
  thumbnail: string | null;
  juliaHind: string | null;

  constructor(o: any = {}) {
    this.image = o['image'] ?? null;
    this.juliaHead = o['juliaHead'] ?? null;
    this.thumbnail = o['thumbnail'] ?? null;
    this.juliaHind = o['juliaHind'] ?? null;
  }
}

export interface RecordingSeverityAnnotations {
  impactSeverity: AnalysisAnnotationReference | null;
  pushOffSeverity: AnalysisAnnotationReference | null;
  rangeDefinition: AnalysisAnnotationReference | null;
  nStrides: AnalysisAnnotationReference | null;
}

export class RecordingSeverityAnnotationsClass implements RecordingSeverityAnnotations {
  impactSeverity: AnalysisAnnotationReference | null;
  pushOffSeverity: AnalysisAnnotationReference | null;
  rangeDefinition: AnalysisAnnotationReference | null;
  nStrides: AnalysisAnnotationReference | null;

  constructor(o: any = {}) {
    this.impactSeverity = o['impactSeverity'] ?? null;
    this.pushOffSeverity = o['pushOffSeverity'] ?? null;
    this.rangeDefinition = o['rangeDefinition'] ?? null;
    this.nStrides = o['nStrides'] ?? null;
  }
}

export interface RecordingBodyAnnotationReferences {
  head: RecordingSeverityAnnotations | null;
  hind: RecordingSeverityAnnotations | null;
}

export class RecordingBodyAnnotationReferencesClass implements RecordingBodyAnnotationReferences {
  head: RecordingSeverityAnnotations | null;
  hind: RecordingSeverityAnnotations | null;

  constructor(o: any = {}) {
    this.head = o['head'] ? new RecordingSeverityAnnotationsClass(o['head']) : null;
    this.hind = o['hind'] ? new RecordingSeverityAnnotationsClass(o['hind']) : null;
  }
}

export type SurfaceType = 'hard' | 'soft' | null;

export interface RecordingAnalysisSummary {
  computationVersion: string | null;
  hasRider: boolean | null;
  head: RecordingBodyVariables | null;
  hind: RecordingBodyVariables | null;
  mainGait: GaitType | null;
  gaitsToPresent: string[];
  annotations: RecordingBodyAnnotationReferences | null;
  isFlexion: boolean | null;
  surface: SurfaceType | null;
  scaling?: RecordingAnalysisScaling;
}

export class RecordingAnalysisSummaryClass implements RecordingAnalysisSummary {
  computationVersion: string | null;
  hasRider: boolean | null;
  head: RecordingBodyVariables | null;
  hind: RecordingBodyVariables | null;
  mainGait: GaitType | null;
  gaitsToPresent: string[];
  annotations: RecordingBodyAnnotationReferences | null;
  isFlexion: boolean | null;
  surface: SurfaceType | null;
  scaling?: RecordingAnalysisScaling;

  constructor(o: any = {}) {
    this.computationVersion = o['computationVersion'] ?? null;
    this.hasRider = o['hasRider'] ?? null;
    this.head = o['head'] ? new RecordingBodyVariablesClass(o['head']) : null;
    this.hind = o['hind'] ? new RecordingBodyVariablesClass(o['hind']) : null;
    this.mainGait = o['mainGait'] ?? null;
    this.gaitsToPresent = o['gaitsToPresent'] ?? [];
    this.annotations = o['annotations'] ? new RecordingBodyAnnotationReferencesClass(o['annotations']) : null;
    this.isFlexion = o['isFlexion'] ?? null;
    this.surface = o['surface'] ?? null;
    this.scaling = o['scaling'] ?? null;
  }
}

export interface RecordingBodyVariables {
  horseRelativeScaling: RecordingVariables;
  meanExtremeDiffScaling: RecordingVariables;
  numberOfStrides: number | null;
}

export class RecordingBodyVariablesClass {
  horseRelativeScaling: RecordingVariables;
  meanExtremeDiffScaling: RecordingVariables;
  numberOfStrides: number | null;

  constructor(o: any = {}) {
    this.horseRelativeScaling = new RecordingVariablesClass(
      o['horseRelativeScaling'] ?? {}
    );
    this.meanExtremeDiffScaling = new RecordingVariablesClass(
      o['meanExtremeDiffScaling'] ?? {}
    );
    this.numberOfStrides = o['numberOfStrides'] ?? null;
  }
}

export interface RecordingVariables {
  impactMean: number | null;
  pushOffMean: number | null;
}

export class RecordingVariablesClass implements RecordingVariables {
  impactMean: number | null;
  pushOffMean: number | null;

  constructor(o: any = {}) {
    this.impactMean = o['impactMean'] ?? null;
    this.pushOffMean = o['pushOffMean'] ?? null;
  }
}
